// PublicConfigService — fetches the public, read-only whitelisted config once
// at app boot (via APP_INITIALIZER in main.ts) and caches it. Non-admin users
// can read display-only flags (currently analysis.layout) this way, since the
// admin /api/admin/settings read is admin-only.
//
// Safe by design: load() NEVER rejects. If the endpoint is unreachable or
// returns garbage, the cache stays empty and callers fall back to their own
// default (the Analysis page defaults to 'classic' when the flag is absent).

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

export type AnalysisLayout = 'bullet' | 'classic' | 'v2';
export type TradeWidgetMode = 'payoff' | 'classic';
export type BriefLayout = 'v2' | 'classic';

@Injectable({ providedIn: 'root' })
export class PublicConfigService {
  private readonly url = '/api/public-config';
  private config: Record<string, string> = {};
  private loaded = false;

  constructor(private readonly http: HttpClient) {}

  /** Fetch + cache the public config. Resolves (never rejects) so it is safe
   *  as an APP_INITIALIZER — a failed fetch leaves defaults in place. */
  async load(): Promise<void> {
    try {
      const cfg = await firstValueFrom(
        this.http.get<Record<string, string>>(this.url),
      );
      this.config = cfg && typeof cfg === 'object' ? cfg : {};
    } catch {
      this.config = {};
    } finally {
      this.loaded = true;
    }
  }

  /** Raw string value for a key, or null when absent/unloaded. */
  get(key: string): string | null {
    return this.config[key] ?? null;
  }

  /** Analysis page layout mode. Defaults to 'classic' when the flag is missing,
   *  unreadable, or an unexpected value — never a blank screen. */
  analysisLayout(): AnalysisLayout {
    const v = this.config['analysis.layout'];
    // BRIEF_ANALYSIS_REDESIGN_P1_SPEC §3.1 (RDS-10) — 'v2' is the redesigned
    // Analysis page; unknown values still fall back to 'classic'.
    if (v === 'bullet' || v === 'v2') return v;
    return 'classic';
  }

  /** Trade display mode on the Analysis page. Defaults to 'classic' when the
   *  flag is missing, unreadable, or an unexpected value — never a blank
   *  screen. 'payoff' renders the payoff-at-expiry diagram card. */
  tradeWidget(): TradeWidgetMode {
    return this.config['analysis.tradeWidget'] === 'payoff' ? 'payoff' : 'classic';
  }

  /** Morning Brief layout (BRIEF_ANALYSIS_REDESIGN_P1_SPEC §3.1, brief.layout).
   *  Defaults to 'classic' when the flag is missing, unreadable, or an
   *  unexpected value — the classic Brief stays the default until QA PASS. */
  briefLayout(): BriefLayout {
    return this.config['brief.layout'] === 'v2' ? 'v2' : 'classic';
  }

  get isLoaded(): boolean {
    return this.loaded;
  }
}
